import { DialogOptions } from "@tableau/extensions-api-types";

export class TableauSettingsService {
  private cachedSettings: Record<string, unknown> = {};

  constructor() {}

  get isInEditMode() {
    return (
      tableau.extensions.environment.mode === tableau.ExtensionMode.Authoring
    );
  }

  private verifyEditMode() {
    if (!this.isInEditMode) {
      throw new Error("Settings can only be modified in edit mode");
    }
  }

  public addListener(
    callback: (settings: Record<string, unknown>) => unknown,
  ): {
    clear: () => void;
  } {
    const unregister = tableau.extensions.settings.addEventListener(
      tableau.TableauEventType.SettingsChanged,
      () => callback(this.updateCachedSettings()),
    );

    return {
      clear: () => unregister(),
    };
  }

  public async initializeEditor() {
    await tableau.extensions.initializeDialogAsync();
    this.updateCachedSettings();
  }

  public clearAllSettings(): void {
    this.verifyEditMode();
    const settings = tableau.extensions.settings.getAll();

    Object.keys(settings).forEach((key) => {
      tableau.extensions.settings.erase(key);
    });
  }

  public async putSetting<T>(key: string, value: T): Promise<T> {
    this.verifyEditMode();
    tableau.extensions.settings.set(key, JSON.stringify(value));
    return value;
  }

  public async putSettings(
    newSettings: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    Object.entries(newSettings).forEach(([key, value]) => [
      key,
      this.putSetting(key, value),
    ]);
    return this.cachedSettings;
  }

  public async findSetting(key: string): Promise<unknown | undefined> {
    return this.cachedSettings[key];
  }

  private updateCachedSettings() {
    const settings = tableau.extensions.settings.getAll();
    this.cachedSettings = Object.fromEntries(
      Object.entries(settings).map(([key, value]) => [key, JSON.parse(value)]),
    );
    return this.cachedSettings;
  }

  public async getAllSettings(): Promise<Record<string, unknown>> {
    return this.updateCachedSettings();
  }

  public async deleteSetting(key: string): Promise<void> {
    this.verifyEditMode();
    return tableau.extensions.settings.erase(key);
  }

  public async saveSettings(): Promise<void> {
    this.verifyEditMode();
    await tableau.extensions.settings.saveAsync();
  }

  public displayDialog(
    url: string,
    dialogOptions: DialogOptions,
  ): Promise<string> {
    return tableau.extensions.ui.displayDialogAsync(url, "", dialogOptions);
  }

  public closeDialog(): void {
    return tableau.extensions.ui.closeDialog("");
  }
}
